/**
 * プロフィールのタブ / メディア種別の定義と、**URL からの正規化**。
 *
 * タブとメディアは URL のクエリ(`?tab=&media=`)が唯一の正とする。
 * 以前は state に持ち、切り替え時に URL へ書いてから `useEffect([location.search])`
 * で読み戻していた。往復するので1操作で2レンダー走り、初期値の算出ロジックが
 * 「初期 state」「URL 監視 effect」「切替ハンドラ」の3箇所に重複していた。
 *
 * ここに置いた resolve 関数を**レンダー中に**通せば、state も同期 effect も要らない。
 */

export type MediaType = 'game' | 'anime' | 'movie';

export const MEDIA_TYPES: MediaType[] = ['game', 'anime', 'movie'];

// 切替バーの件数に数えるステータス。status_counts の添字はステータス番号-1
// (添字3 = ステータス4 = 積みゲー)。
// 積み(4)とウィッシュリスト(5)は「まだ触れていないもの」なので全メディアで除外する。
// それ以外に何を数えるかはメディアごとに違う。
export const MEDIA_COUNT_STATUSES: Record<MediaType, number[]> = {
  game: [0, 1, 2],  // プレイ済み / プレイ中 / 中断
  anime: [0, 1],    // 視聴済み / 視聴中
  movie: [0],       // 視聴済み（映画に「視聴中」「中断」はほぼ無い）
};

// 自分のページで最後に見ていたメディア。端末ローカル設定。
export const MEDIA_TAB_STORAGE_KEY = 'libraryMediaTab';

// 拡張ライブラリ(アニメ/映画)の切替バーを自分で非表示にしたか。端末ローカル設定。
// 非表示にできるのは拡張ライブラリのデータが無いときだけなので、1件でも登録すれば
// このフラグに関わらずバーは再表示される（＝データを持つのにバーが無い状態は作れない）。
export const EXT_BAR_HIDDEN_KEY = 'extLibraryBarHidden';

export const MAIN_TABS = {
  PROFILE: 'profile',
  LIBRARY: 'library',
  LISTS: 'lists',
  REVIEWS: 'reviews',
  MEMORIES: 'memories',
  ACTIVITY: 'activity',
  FOLLOWS: 'follows',
  TIMELINE: 'timeline',
  PLAYLOGS: 'playlogs',
  POSTS: 'posts',
  GOTY: 'goty',
  GALLERY: 'gallery',
} as const;

export type MainTabType = typeof MAIN_TABS[keyof typeof MAIN_TABS];

export const ALL_TABS = Object.values(MAIN_TABS) as MainTabType[];

// アニメ / 映画で成立するタブ。それ以外はゲーム専用。
const EXT_MEDIA_TABS: MainTabType[] = [MAIN_TABS.PROFILE, MAIN_TABS.LIBRARY, MAIN_TABS.REVIEWS];

// 自分のページだけのタブ。他人のページでは選べない
// (タブ自体を描いていないので、URL で来ても選択状態が空になってしまう)。
const OWN_ONLY_TABS: MainTabType[] = [MAIN_TABS.TIMELINE];

/** メディアと閲覧者の立場から、実際に出せるタブか */
export const isTabAvailable = (
  tab: MainTabType,
  media: MediaType,
  isPublic: boolean,
): boolean => {
  if (media !== 'game' && !EXT_MEDIA_TABS.includes(tab)) return false;
  if (isPublic && OWN_ONLY_TABS.includes(tab)) return false;
  return true;
};

/**
 * `?media=` を正規化する。
 *
 * 自分のページでは指定が無ければ端末に覚えたメディアへ戻す（作業の続きのため）。
 * 他人のページでは常にゲームから始める（その人の何を見たいかは URL でしか分からない）。
 */
export const resolveMedia = (param: string | null, isPublic: boolean): MediaType => {
  if (param && MEDIA_TYPES.includes(param as MediaType)) return param as MediaType;
  if (!isPublic) {
    const stored = safeLocalGet(MEDIA_TAB_STORAGE_KEY);
    if (stored && MEDIA_TYPES.includes(stored as MediaType)) return stored as MediaType;
  }
  return 'game';
};

/**
 * `?tab=` を正規化する。
 *
 * 既定は他人のページならプロフィール、自分のページならライブラリ。
 * 知らない値や、そのメディア / 立場では出せないタブは既定へ落とす
 * (落とさないと Tabs の value がどのタブとも一致せず、選択中の下線が消える)。
 */
export const resolveTab = (
  param: string | null,
  media: MediaType,
  isPublic: boolean,
): MainTabType => {
  const fallback = isPublic ? MAIN_TABS.PROFILE : MAIN_TABS.LIBRARY;
  const tab = ALL_TABS.includes(param as MainTabType) ? (param as MainTabType) : fallback;
  if (isTabAvailable(tab, media, isPublic)) return tab;

  // **落とし先は「出せない理由」で変える。**
  // 立場の問題(他人のページでタイムラインを指定された等)なら、そのページの既定へ。
  // 他人のページの既定はプロフィールなので、ライブラリへ落とすと的が外れる。
  if (isPublic && OWN_ONLY_TABS.includes(tab)) return fallback;

  // メディアの問題ならライブラリへ。
  // 「ゲームのメモリーを見ていた → アニメへ切替」のような移動でも文脈が近い。
  return isTabAvailable(MAIN_TABS.LIBRARY, media, isPublic) ? MAIN_TABS.LIBRARY : fallback;
};

/** `?list_page=` を正規化する。1未満や数値でない値は1ページ目 */
export const resolveListPage = (param: string | null): number => {
  const page = parseInt(param || '1', 10);
  return Number.isFinite(page) && page > 0 ? page : 1;
};

// localStorage は Safari のプライベートモード等で例外を投げることがある。
// 端末設定が読めないだけで画面が落ちるのは割に合わない。
export function safeLocalGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function safeLocalSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* 覚えられないだけなので黙って諦める */
  }
}
